import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Typography,
  styled,
  tableCellClasses,
  IconButton,
  Snackbar,
  SnackbarContent,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import utils from '../../utils';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#333333',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    fontSize: '1.1rem',
    textAlign: 'center',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '1rem',
    textAlign: 'center',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function OpRichiesteCba() {
  const [requests, setRequests] = useState([]);
  const [ragioniSociali, setRagioniSociali] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchRagioniSociali = async () => {
      try {
        const res = await axios.get(`/api/aziendeeop`, {
          headers: utils.getAuthHeaders(),
        });
        const { success, error } = res.data;
        if (success) {
          const dizionario = {};
          for (const az of res.data.aziende) {
            dizionario[az.id] = { nome: az.nome, email: az.email };
          }
          setRagioniSociali(dizionario);
        } else {
          setError(error);
        }
      } catch (error) {
        setError(
          "Si è verificato un errore imprevisto sul nostro server."
        );
        console.log(error);
      }
    }
    fetchRagioniSociali();
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const res = await axios.get('/api/richiestecba', {
        headers: utils.getAuthHeaders(),
      });
      const { success, error, requests } = res.data;
      if (success) {
        setRequests(requests);
      } else {
        setError(error);
      }
    } catch (error) {
      setError("Si è verificato un errore imprevisto sul nostro server.");
      console.log(error);
    }
  };

  const handleCheckboxChange = async (id, ricontattata) => {
    try {
      const res = await axios.post(`/api/setricontattatacba`, { id, ricontattata }, {
        headers: utils.getAuthHeaders(),
      });
      const { success, error } = res.data;
      if (success) {
        const updatedRequests = [...requests];
        setRequests(updatedRequests.map(r => { if (r.id != id) { return r } else { return { ...r, ricontattata } } }));
      } else {
        setError(error);
      }
    } catch (error) {
      setError("Si è verificato un errore imprevisto sul nostro server.");
      console.log(error);
    }
  };

  const handleFileUpload = async (event, reqId, userId) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("doc", file);
      formData.append("data", JSON.stringify({
        id: reqId,
        doc: file.name,
        userId,
      }));

      const res = await axios.post("/api/caricadoccba", formData, {
        headers: {
          ...utils.getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      const { success, error, id } = res.data;
      if (success) {
        const updatedRequests = [...requests];
        setRequests(updatedRequests.map(r => { if (r.id != reqId) { return r } else { return { ...r, documento: id } } }));
        setSuccessMessage("Documento caricato con successo");
      } else {
        setError(error);
      }
    } catch (err) {
      setError("Si è verificato un errore imprevisto sul nostro server.");
      console.error("Errore upload file", err);
    }
  };

  const handleFileDownload = async (docId) => {
    try {
      await utils.opDownload(docId);
    } catch (error) {
      setError('File non trovato');
      console.error('Errore nel download della busta paga:', error);
    }
  };

  const handleFileView = (docId) => {
    try {
      utils.opOpenPdfInNewTab(docId);
    } catch (error) {
      setError('Errore nell\'anteprima del PDF: ' + error.message);
      console.error('Errore nell\'anteprima della busta paga:', error);
    }
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom align="center" style={{ marginTop: '20px' }}>
        Richieste CBA delle Aziende
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>ID</StyledTableCell>
              <StyledTableCell>Data Richiesta</StyledTableCell>
              <StyledTableCell>Ragione Sociale</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Ricontattata</StyledTableCell>
              <StyledTableCell>Documenti</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request, index) => (
              <StyledTableRow key={request.id}>
                <StyledTableCell>{request.id}</StyledTableCell>
                <StyledTableCell>{new Date(request.timestamp).toLocaleDateString()}</StyledTableCell>
                <StyledTableCell>{ragioniSociali[request.utente_id]?.nome}</StyledTableCell>
                <StyledTableCell>{ragioniSociali[request.utente_id]?.email}</StyledTableCell>
                <StyledTableCell>
                  <Checkbox
                    icon={<CheckCircleIcon style={{ color: 'grey' }} />}
                    checkedIcon={<CheckCircleIcon style={{ color: 'green' }} />}
                    checked={request.ricontattata}
                    onChange={() => handleCheckboxChange(request.id, !request.ricontattata)}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <IconButton component="label">
                    <CloudUploadIcon />
                    <input
                      type="file"
                      hidden
                      onChange={(event) => handleFileUpload(event, request.id, request.utente_id)}
                    />
                  </IconButton>
                  {request.documento && (
                    <>
                      <IconButton onClick={() => handleFileDownload(request.documento)}>
                        <GetAppIcon />
                      </IconButton>
                      <IconButton onClick={() => handleFileView(request.documento)}>
                        <VisibilityIcon />
                      </IconButton>
                    </>
                  )}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Snack successo Verde */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarContent
          sx={{
            backgroundColor: 'green',
            color: 'white',
            fontWeight: 'bold',
          }}
          message={successMessage}
        />
      </Snackbar>
      {/* Snack per Errori Rosso */}
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarContent
          sx={{
            backgroundColor: 'red',
            color: 'white',
            fontWeight: 'bold',
          }}
          message={error}
        />
      </Snackbar>
    </Container>
  );
}

export default OpRichiesteCba;
